/* ============================================================
   Edge Compute console — state store, API client, router
   Components read the module-level `DB` directly; the App root
   subscribes to the store and re-renders the tree on every change.
   ============================================================ */
import { useSyncExternalStore } from "react";
import { ago, dur, when } from "./fmt";

export type Any = any;
const EMPTY = () => ({
  org: {}, networks: [], sites: [], nodes: [], unclaimed: [], workloads: [], stacks: [], containers: [], jobs: [], alerts: [],
  rules: [], clusters: [], images: [], playbooks: [], catalog: [], backups: [], users: [], audit: [], recycle: [], rollouts: [],
  schedules: [], blueprints: [], settings: {}, meta: {}, me: {},
});
export let DB: Any = EMPTY();

/* ---------- tiny external store: one version counter for the whole app ---------- */
let version = 0;
const subs = new Set<() => void>();
export function rerender() { version++; subs.forEach((f) => f()); }
export function useStore() {
  return useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => version);
}
/* generic mini-store for other singletons (toasts, modal, menu, auth) */
export function createStore<T>(init: T) {
  let v = init;
  const ls = new Set<() => void>();
  return {
    get: () => v,
    set(n: T | ((o: T) => T)) { v = typeof n === "function" ? (n as (o: T) => T)(v) : n; ls.forEach((f) => f()); },
    use(): T { return useSyncExternalStore((f) => { ls.add(f); return () => ls.delete(f); }, () => v); },
  };
}

/* ---------- lookups ---------- */
export const node = (id: Any) => DB.nodes.find((n: Any) => String(n.id) === String(id));
export const site = (id: Any) => DB.sites.find((s: Any) => String(s.id) === String(id)) || { name: "Unassigned", id: null };
export const wl = (id: Any) => DB.workloads.find((w: Any) => String(w.id) === String(id));
export const stackById = (id: Any) => DB.stacks.find((s: Any) => String(s.id) === String(id));
export const ctrById = (id: Any) => DB.containers.find((c: Any) => String(c.id) === String(id));
export const nodeOf = (w: Any) => node(w.node) || { name: w.external ? "direct" : "—", site: null, status: "unknown", settings: {} };
export const siteNameOf = (w: Any) => site(nodeOf(w).site).name;
const ROLES = ["Viewer", "Operator", "Admin", "Owner"];
export const can = (role: string) => ROLES.indexOf(DB.me.role) >= ROLES.indexOf(role);

/* ---------- hooks other modules register (avoids import cycles) ---------- */
export const hooks: {
  toast: (msg: string, sub?: string, kind?: string) => void;
  signin: (msg?: string) => void;
  mfa: (forced?: boolean) => void;
  closeModal: () => void;
} = { toast: () => {}, signin: () => {}, mfa: () => {}, closeModal: () => {} };
export const toast = (msg: string, sub?: string, kind?: string) => hooks.toast(msg, sub, kind);

/* ---------- API ---------- */
export async function api(method: string, path: string, body?: Any, opts: { noAuthRedirect?: boolean } = {}): Promise<Any> {
  const r = await fetch(path, {
    method, credentials: "same-origin",
    headers: body !== undefined ? { "Content-Type": "application/json" } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (r.status === 401 && !opts.noAuthRedirect) { hooks.signin(); throw new Error("Please sign in"); }
  const ct = r.headers.get("content-type") || "";
  const data = ct.includes("json") ? await r.json() : await r.text();
  if (!r.ok) {
    let msg: Any = (data && data.detail) || r.statusText;
    if (Array.isArray(msg)) msg = msg.map((x: Any) => (x.loc || []).slice(-1)[0] + ": " + x.msg).join("; ");
    if (r.status === 403 && String(msg).includes("two-factor")) hooks.mfa(true);
    throw new Error(msg);
  }
  return data;
}
/* run an action that creates a job: toast + jump to the job page */
export async function job(method: string, path: string, body?: Any, { go: goTo = true, quiet = false } = {}) {
  try {
    const r = await api(method, path, body);
    hooks.closeModal();
    if (r && r.job) {
      if (!quiet) toast(r.status === "queued" ? "Job queued" : "Job started", r.job);
      await load();
      if (goTo) go("job", r.job);
    } else await load();
    return r;
  } catch (e: Any) { toast("Could not start", e.message, "err"); throw e; }
}
export async function act(method: string, path: string, body?: Any, okMsg?: string, sub?: string) {
  try { const r = await api(method, path, body); if (okMsg) toast(okMsg, sub || ""); await load(); return r; }
  catch (e: Any) { toast("Failed", e.message, "err"); throw e; }
}

/* ---------- live state ---------- */
export function normalize(d: Any) {
  d.nodes.forEach((n: Any) => { n.lastSeen = ago(n.last_seen); n.uptimeTxt = dur(n.uptime); n.settings = n.settings || {}; });
  d.workloads.forEach((w: Any) => { w.uptimeTxt = w.status === "running" ? dur(w.uptime) : "—"; });
  d.jobs.forEach((j: Any) => {
    j.startedTxt = j.started ? when(j.started) : j.status === "queued" ? "—" : when(j.created);
    const end = j.finished ? new Date(j.finished) : j.started && j.status === "running" ? new Date() : null;
    j.dur = j.started && end ? dur((+end - +new Date(j.started)) / 1000) : "—";
  });
  d.stacks.forEach((s: Any) => { s.updatedTxt = ago(s.updated); });
  return d;
}
export function setDB(d: Any) { DB = normalize(d); rerender(); }
export function renormalize() { if (DB.me && DB.me.id) { DB = normalize(DB); rerender(); } }
let loading: Promise<void> | null = null;
export function load(): Promise<void> {
  if (loading) return loading;
  loading = (async () => {
    try { DB = normalize(await api("GET", "/api/state")); }
    finally { loading = null; }
    rerender();
  })();
  return loading;
}

/* ---------- router: #/route/param/tab ---------- */
export const nav: { route: string; param: string | null; tab: string | null } = { route: "home", param: null, tab: null };
/* page-local UI state that must survive re-renders (selection, expanded rows, filters, sub-tabs) */
export const ui: { sel: Set<string>; expanded: Set<string>; filter: Any; sub: Any } = { sel: new Set(), expanded: new Set(), filter: {}, sub: {} };
export const railOpen = createStore(false);
export function go(route: string, param?: Any, tab?: string | null) {
  nav.route = route; nav.param = param == null ? null : String(param); nav.tab = tab || null; ui.sel.clear();
  const h = "#/" + route + (param != null ? "/" + encodeURIComponent(param) : "") + (tab ? "/" + tab : "");
  if (location.hash !== h) history.pushState(null, "", h);
  railOpen.set(false);
  rerender();
  document.getElementById("view")?.scrollTo(0, 0);
}
export function fromHash() {
  const p = location.hash.replace(/^#\/?/, "").split("/");
  if (p[0].startsWith("invite=")) return;
  nav.route = p[0] || "home"; nav.param = p[1] ? decodeURIComponent(p[1]) : null; nav.tab = p[2] || null;
}
export function search(q: string) {
  if (!q) { if (nav.route === "search") go("workloads"); return; }
  nav.route = "search"; nav.param = q; rerender();
}
export function toggle(id: string) { ui.expanded.has(id) ? ui.expanded.delete(id) : ui.expanded.add(id); rerender(); }

export const wsUrl = (path: string) => `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}${path}`;

/* fire-and-forget for onClick handlers: job()/act() already toast their own errors */
export const fire = (p: Promise<Any> | Any) => { if (p && typeof p.catch === "function") p.catch(() => {}); };
